import type { ReactNode, SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement> & {
  children?: ReactNode;
  size?: number;
};

function Icon({ children, size = 16, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height={size}
      viewBox="0 0 24 24"
      width={size}
      {...props}
    >
      {children}
    </svg>
  );
}

export function UploadIcon({ size = 20 }: { size?: number }) {
  return (
    <Icon size={size} stroke="currentColor" strokeWidth="1.7">
      <path d="M12 16V4M7 9l5-5 5 5" />
      <path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
    </Icon>
  );
}

export function FileIcon({ size = 15 }: { size?: number }) {
  return (
    <Icon size={size} stroke="currentColor" strokeWidth="1.7">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </Icon>
  );
}

export function TableIcon({ size = 15 }: { size?: number }) {
  return (
    <Icon size={size} stroke="currentColor" strokeWidth="1.7">
      <rect height="18" width="18" x="3" y="3" />
      <path d="M9 3v18M15 3v18" />
    </Icon>
  );
}

export function SearchIcon({ size = 15 }: { size?: number }) {
  return (
    <Icon size={size} stroke="currentColor" strokeWidth="1.7">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-4.35-4.35" />
    </Icon>
  );
}

export function ChevronIcon({
  direction = "right",
  size = 14,
}: {
  direction?: "left" | "right" | "down";
  size?: number;
}) {
  const path = direction === "left"
    ? "M15 18l-6-6 6-6"
    : direction === "down"
      ? "M6 9l6 6 6-6"
      : "M9 18l6-6-6-6";
  return (
    <Icon size={size} stroke="currentColor" strokeWidth="2">
      <path d={path} />
    </Icon>
  );
}

export function CloseIcon({ size = 16 }: { size?: number }) {
  return (
    <Icon size={size} stroke="currentColor" strokeWidth="1.9">
      <path d="M18 6L6 18M6 6l12 12" />
    </Icon>
  );
}

export function PlusIcon({ size = 15 }: { size?: number }) {
  return (
    <Icon size={size} stroke="currentColor" strokeWidth="1.9">
      <path d="M12 5v14M5 12h14" />
    </Icon>
  );
}

export function TrashIcon({ size = 15 }: { size?: number }) {
  return (
    <Icon size={size} stroke="currentColor" strokeWidth="1.7">
      <path d="M4 7h16M9 7V4h6v3M6 7l1 14h10l1-14" />
    </Icon>
  );
}

export function ChatIcon({ size = 15 }: { size?: number }) {
  return (
    <Icon size={size} stroke="currentColor" strokeWidth="1.7">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </Icon>
  );
}

export function CheckIcon({ size = 15 }: { size?: number }) {
  return (
    <Icon size={size} stroke="currentColor" strokeWidth="2.4">
      <path d="M20 6L9 17l-5-5" />
    </Icon>
  );
}

export function StarIcon({ filled = false }: { filled?: boolean }) {
  return (
    <Icon
      fill={filled ? "currentColor" : "none"}
      size={15}
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" />
    </Icon>
  );
}

export function DragIcon() {
  return (
    <Icon fill="currentColor" size={14}>
      <circle cx="9" cy="6" r="1.4" />
      <circle cx="15" cy="6" r="1.4" />
      <circle cx="9" cy="12" r="1.4" />
      <circle cx="15" cy="12" r="1.4" />
      <circle cx="9" cy="18" r="1.4" />
      <circle cx="15" cy="18" r="1.4" />
    </Icon>
  );
}
